import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { drawElements } from '../../elements/layout_elements.ts';
import { lastValue } from '../../values/readings.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { useTapCommand } from '../../view/tap_command.ts';
import { TILE_FONT } from '../../view/theme.ts';
import { TileStyleOverrideContext, useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { DEFAULT_VALUE_COLOR } from '../value/value_tile.ts';
import { ModePicker } from './mode_picker.tsx';
import { modeChoices, stateValueOf, statusTileContent, statusTileElements, type ModeChoice, type StatusTile } from './status_tile.ts';

/**
 * The content area of a `status` tile (docs/specs/status-tile.md): its source's value as a state.
 * With a command, a tap opens the mode card, and a state chosen there is sent and shown at once.
 */
export function StatusTileView({ tile }: { tile: StatusTile }) : ReactNode {
    const { source, command } = tile.options;
    const environment = useTileEnvironment();
    const value = lastValue(environment.useReadings(tile, source));
    const tap = useTapCommand(tile, source);
    const content = statusTileContent(tile, value, environment.locale, useTileTextColor(DEFAULT_VALUE_COLOR));
    const icon = useLegacyIcon(content.show === 'text' ? undefined : content.icon);
    const area = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<DOMRect | null>(null);

    // The state's tile style over the tile's own (QS-4), as a value tile's style rules.
    const setOverride = useContext(TileStyleOverrideContext);
    const styleKey = JSON.stringify(content.style ?? null);
    useEffect(() => {
        setOverride(styleKey === 'null' ? undefined : (JSON.parse(styleKey) as typeof content.style));
    }, [styleKey, setOverride]);
    useEffect(() => () => setOverride(undefined), [setOverride]);

    const choose = (choice: ModeChoice) : void => {
        setAnchor(null);
        if (command !== undefined) {
            // Shown at once: the value the source will read in that state.
            void tap(command, choice.send, [{ value: choice.value }]);
        }
    };
    const open = () : void => {
        if (command !== undefined) {
            setAnchor(area.current?.closest('[data-tile]')?.getBoundingClientRect() ?? area.current?.getBoundingClientRect() ?? null);
        }
    };

    return (
        <div ref={area} style={{ position: 'absolute', inset: 0, cursor: command === undefined ? 'default' : 'pointer' }} onClick={open}>
            <CanvasView deps={[JSON.stringify(content), icon]} draw={(ctx, width, height) => drawElements(ctx, { left: 0, top: 0, width, height }, statusTileElements(content, icon, TILE_FONT))} />
            {anchor && <ModePicker anchor={anchor} title={tile.label ?? 'Mode'} choices={modeChoices(tile)} current={stateValueOf(value)} onChoose={choose} onClose={() => setAnchor(null)} />}
        </div>
    );
}
